import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { FiUpload, FiRefreshCw } from 'react-icons/fi';
import api from '../../services/api';
import Alert from '../Common/Alert';
import Spinner from '../Common/Spinner';
import { useProject } from '../../context/ProjectContext';
import './StsLpCsvImport.css';

const StsLpCsvImport = () => {
  const { currentProject: contextProject } = useProject();
  const currentProject = contextProject && contextProject !== 'all' ? contextProject : null;
  const [file, setFile] = useState(null);
  const [loadingList, setLoadingList] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [rows, setRows] = useState([]);
  const [toast, setToast] = useState(null);
  const [error, setError] = useState(null);

  const loadRows = useCallback(async () => {
    if (!currentProject) {
      setRows([]);
      return;
    }
    try {
      setLoadingList(true);
      setError(null);
      const res = await api.get(
        `/sites/feasibility/sts-lp-import?project=${encodeURIComponent(currentProject)}`
      );
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        'Failed to load imported data';
      setError(msg);
      setRows([]);
    } finally {
      setLoadingList(false);
    }
  }, [currentProject]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!currentProject) {
      setToast({ type: 'info', message: 'Select a project in the header first.' });
      return;
    }
    if (!file) {
      setToast({ type: 'info', message: 'Choose a CSV file.' });
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    try {
      setUploading(true);
      setError(null);
      const url = `/sites/feasibility/sts-lp-import?project=${encodeURIComponent(currentProject)}`;
      const res = await api.post(url, formData, {
        transformRequest: (data, headers) => {
          if (headers && typeof headers === 'object') {
            delete headers['Content-Type'];
          }
          return data;
        },
      });
      const imported = res.data?.imported ?? res.data?.Imported ?? 0;
      const warnings = res.data?.warnings ?? res.data?.Warnings ?? [];
      setToast({
        type: 'success',
        message:
          `Imported ${imported} row(s).` +
          (warnings.length ? ` ${warnings.length} warning(s) — check response.` : '') +
          ' Close and reopen Site Selection to refresh the STS-LP tab.',
      });
      setFile(null);
      const input = document.getElementById('sts-lp-csv-input');
      if (input) input.value = '';
      await loadRows();
    } catch (err) {
      const data = err.response?.data;
      const errList = data?.errors ?? data?.Errors;
      const msg =
        (typeof data === 'string' ? data : data?.message) ||
        (Array.isArray(errList) ? errList.filter(Boolean).join('; ') : null) ||
        err.message ||
        'Upload failed';
      setError(msg);
    } finally {
      setUploading(false);
    }
  };

  const col = (k, alt) => (r) => r[k] ?? r[alt] ?? '';

  return (
    <div className="sts-lp-csv-import">
      {toast && (
        <div className="sts-lp-toast-wrap">
          <Alert type={toast.type} message={toast.message} onClose={() => setToast(null)} />
        </div>
      )}
      

      {!currentProject && (
        <Alert type="info" message="Select a project in the header to import or view data." />
      )}

      {error && (
        <div className="sts-lp-error">
          <Alert type="error" message={error} onClose={() => setError(null)} />
        </div>
      )}

      <div className="card sts-lp-upload-card">
        <h3>Upload CSV</h3>
        <p className="text-muted sts-lp-hint">
          Headers: ProjectCode (must match header project, e.g. 0198-23), SiteCode (optional if PIName and
          SiteName are set), PIName, SiteName, Country, EmailID, OverallRating, IsActive, Crit_* and Sub_*
          columns, OverallScore100, Notes. Tab or comma separated. Disease is taken from the project in
          CTMS, not the file.
        </p>
        <form onSubmit={handleUpload} className="sts-lp-form">
          <input
            id="sts-lp-csv-input"
            type="file"
            accept=".csv,.txt,text/csv"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            disabled={!currentProject || uploading}
          />
          <button
            type="submit"
            className="btn btn-primary"
            disabled={!currentProject || uploading || !file}
          >
            <FiUpload size={18} /> {uploading ? 'Uploading…' : 'Upload & replace'}
          </button>
        </form>
      </div>

      <div className="card sts-lp-table-card">
        <div className="sts-lp-table-toolbar">
          <h3>Imported rows ({rows.length})</h3>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={loadRows}
            disabled={!currentProject || loadingList}
          >
            <FiRefreshCw size={16} /> Refresh
          </button>
        </div>
        {loadingList ? (
          <Spinner />
        ) : rows.length === 0 ? (
          <p className="text-muted">No STS-LP import for this project.</p>
        ) : (
          <div className="table-responsive sts-lp-table-wrap">
            <table className="sites-table sts-lp-data-table">
              <thead>
                <tr>
                  <th>LP id</th>
                  <th>PI</th>
                  <th>Site name</th>
                  <th>Country</th>
                  <th>Email</th>
                  <th>Rating</th>
                  <th>Uploaded (UTC)</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id ?? r.Id}>
                    <td>
                      {(() => {
                        const id = r.id ?? r.Id;
                        return id != null && id !== '' ? `LP-${id}` : '—';
                      })()}
                    </td>
                    <td>{col('piName', 'PiName')(r)}</td>
                    <td>{col('siteName', 'SiteName')(r)}</td>
                    <td>{col('country', 'Country')(r)}</td>
                    <td>{col('emailID', 'EmailID')(r) || col('emailId', 'EmailId')(r)}</td>
                    <td className="text-center">{col('overallRating', 'OverallRating')(r)}</td>
                    <td className="text-muted small">
                      {(() => {
                        const u = r.uploadedUtc ?? r.UploadedUtc;
                        return u ? String(u).replace('T', ' ').slice(0, 19) : '—';
                      })()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default StsLpCsvImport;
